import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FiMenu, FiX, FiHome, FiFileText, FiLogOut, FiBarChart2, FiMapPin, FiSettings } from 'react-icons/fi';
import Footer from './Footer';
import { HtmlHead } from '../HtmlHead';

const Document = ({ children }) => (
  <html lang="pt-BR">
    <head>
      <HtmlHead />
    </head>
    <body>
      {children}
    </body>
  </html>
);

const MainLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    const { success } = await logout();
    if (success) {
      navigate('/login');
    }
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  return (
    <Document>
      <div className="flex h-screen bg-gray-100">
        {/* Mobile sidebar backdrop */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          ></div>
        )}

        {/* Sidebar */}
        <aside 
          className={`fixed inset-y-0 left-0 z-30 w-64 transform bg-white shadow-lg transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex flex-col h-full">
            {/* Sidebar header */}
            <div className="flex items-center justify-between px-4 py-5 border-b">
              <div className="flex items-center space-x-2">
                <img src="/images/logo192.png" alt="Logo" className="w-8 h-8" />
                <span className="text-xl font-semibold text-[#20245c]">RIDE-DF</span>
              </div>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="p-2 rounded-md lg:hidden hover:bg-gray-100"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            {/* Sidebar content */}
            <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
              <Link
                to="/forms"
                className={`flex items-center px-4 py-3 text-gray-700 rounded-md ${
                  isActive('/forms') ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-100'
                }`}
                onClick={() => setSidebarOpen(false)}
              >
                <FiFileText className="w-5 h-5 mr-3" />
                <span>Formulários</span>
              </Link>

              {user?.role === 'admin' && (
                <>
                  <Link
                    to="/dashboard"
                    className={`flex items-center px-4 py-3 text-gray-700 rounded-md ${
                      isActive('/dashboard') ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <FiBarChart2 className="w-5 h-5 mr-3" />
                    <span>Dashboard</span>
                  </Link>
                  <Link
                    to="/municipios"
                    className={`flex items-center px-4 py-3 text-gray-700 rounded-md ${
                      isActive('/municipios') ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <FiMapPin className="w-5 h-5 mr-3" />
                    <span>Municípios</span>
                  </Link>
                  <Link
                    to="/admin/forms"
                    className={`flex items-center px-4 py-3 text-gray-700 rounded-md ${
                      isActive('/admin/forms') ? 'bg-primary-50 text-primary-700' : 'hover:bg-gray-100'
                    }`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <FiSettings className="w-5 h-5 mr-3" />
                    <span>Gerenciar Formulários</span>
                  </Link>
                </>
              )}
            </nav>

            {/* Sidebar footer */}
            <div className="px-4 py-4 border-t">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-primary-700 font-medium">
                      {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700 truncate">
                    {user?.name || user?.email || 'Usuário'}
                  </p>
                  <p className="text-xs text-gray-500 truncate">
                    {user?.role || 'Usuário'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center w-full px-4 py-2 text-gray-700 rounded-md hover:bg-gray-100"
              >
                <FiLogOut className="w-5 h-5 mr-3" />
                <span>Sair</span>
              </button>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex flex-col flex-1 overflow-hidden">
          {/* Top navbar */}
          <header className="bg-white shadow-sm lg:hidden">
            <div className="flex items-center justify-between px-4 py-4">
              <button
                onClick={() => setSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <FiMenu className="w-6 h-6" />
              </button>
              <span className="text-lg font-semibold text-[#2596be]">RIDE-DF</span>
              <div className="w-6"></div> {/* Empty div for flex alignment */}
            </div>
          </header>

          {/* Page content */}
          <main className="flex-1 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </main>
          <Footer />
        </div>
      </div>
    </Document>
  );
};

export default MainLayout;
